import React, { useState, useEffect } from 'react';
import './UserManagement.css';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty } from 'lodash';
import UserLeftSection from './UserLeftSection';
import UserRightSection from './UserRightSection';
import * as action from '../store/User.action';
import Loader from '../../../Components/Utility/Loader/Loader';

function UserManagement() {
  const dispatch = useDispatch();
  const paramId = useParams();
  const { t } = useTranslation('common');
  const UsersReducers = useSelector((reducerstate) => reducerstate);
  const { Users } = UsersReducers;
  const { userGroupsDetails } = Users;
  const [groupsDetails, setGroupsDetails] = useState(null);
  const [groupsDetailsRightSec, setGroupsDetailsRightSec] = useState(null);

  const [rightSidedata, setRightSideData] = useState(null);
  const [leftSidedata, setLeftSideData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState({
    brandVal: true,
    catOneVal: true,
    catTwoVal: true,
    supplierVal: true,
    productVal: true,
  });

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const data = paramId;
      const res = await dispatch(action.getUserGroupsCardListbyid(data));
      res && setIsLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userGroupsDetails) {
      if (!userGroupsDetails.supplier) {
        userGroupsDetails.supplier = [];
      }
      setGroupsDetails(userGroupsDetails);
      setGroupsDetailsRightSec(userGroupsDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroupsDetails]);

  // Live-validate flags so messages disappear as soon as the user makes a valid selection
  useEffect(() => {
    if (!groupsDetails) return;

    const brandFlag = !!groupsDetails?.allBrands || (Array.isArray(groupsDetails?.brand) && groupsDetails?.brand?.length > 0);
    const supplierFlag = !!groupsDetails?.allSuppliers || (Array.isArray(groupsDetails?.supplier) && groupsDetails?.supplier?.length > 0);
    const productFlag = !!groupsDetails?.allProductTypes || (Array.isArray(groupsDetails?.productType) && groupsDetails?.productType?.length > 0);

    const allCategories = !!groupsDetails?.allCategories;
    const catOneSelected = Array.isArray(groupsDetails?.catOne) && groupsDetails?.catOne?.length > 0;
    const catTwoSelected = Array.isArray(groupsDetails?.catTwo) && groupsDetails?.catTwo?.length > 0;

    const catOneFlag = allCategories || catOneSelected;
    const catTwoFlag = allCategories || (catOneSelected && catTwoSelected);

    setState((prev) => ({
      ...prev,
      brandVal: brandFlag,
      catOneVal: catOneFlag,
      catTwoVal: catTwoFlag,
      supplierVal: supplierFlag,
      productVal: productFlag,
    }));
  }, [groupsDetails]);

  const rightSectionData = (data) => {
    if (groupsDetails && data) {
      const rightData = cloneDeep(groupsDetails);
      rightData.rights = setRights(data);
      setRightSideData(rightData);
    }
  };

  const setRights = (array) => {
    // maintain this order its important
    return {
      products: array?.find((dEle) => dEle?.name === 'products').rightsArray,
      libraries: array?.find((dEle) => dEle?.name === 'libraries').rightsArray,
      rangePlan: array?.find((dEle) => dEle?.name === 'rangePlan').rightsArray,
      criticalPath: array?.find((dEle) => dEle?.name === 'criticalPath').rightsArray,
      pim: array?.find((dEle) => dEle?.name === 'pim').rightsArray,
      dam: array?.find((dEle) => dEle?.name === 'dam')?.rightsArray,
      dpp: array?.find((dEle) => dEle?.name === 'dpp')?.rightsArray,
      planning: array?.find((dEle) => dEle?.name === 'planning').rightsArray,
      placeholder: array?.find((dEle) => dEle?.name === 'placeholder').rightsArray,
      supplierOverview: array?.find((dEle) => dEle?.name === 'supplierOverview').rightsArray,
      manuals: array?.find((dEle) => dEle?.name === 'manuals').rightsArray,
      masters: array?.find((dEle) => dEle?.name === 'masters').rightsArray,
    };
  };

  const leftSectionData = (data) => {
    if (data) {
      setLeftSideData(data);
    }
  };

  const brandValidation = () => {
    if (!groupsDetails?.allBrands) {
      return groupsDetails?.brand?.length > 0;
    }
    return true;
  };

  const supplierValidation = () => {
    if (!groupsDetails?.allSuppliers) {
      return groupsDetails?.supplier?.length > 0;
    }
    return true;
  };

  const productValidation = () => {
    // Validate product type only when dependency flag is enabled
    const ptDependency = leftSidedata?.catBasedOnPTData === true;
    if (!ptDependency) return true;

    if (!groupsDetails?.allProductTypes) {
      return groupsDetails?.productType?.length > 0;
    }
    return true;
  };

  const validationFun = () => {
    const brandValidate = brandValidation();
    // categoryValidate was used earlier to derive which category is missing; now we compute flags directly
    const supplierValidate = supplierValidation();
    const productValidate = productValidation();

    // Derive individual category flags so Cat 2 can show its own error even if Cat 1 is also missing
    const allCategories = !!groupsDetails?.allCategories;
    const catOneSelected = Array.isArray(groupsDetails?.catOne) && groupsDetails?.catOne?.length > 0;
    const catTwoSelected = Array.isArray(groupsDetails?.catTwo) && groupsDetails?.catTwo?.length > 0;
    const catOneFlag = allCategories || catOneSelected;
    // Cat 2 should only be valid when Cat 1 is selected as well (under partial access)
    const catTwoFlag = allCategories || (catOneSelected && catTwoSelected);

    setState((prev) => ({
      ...prev,
      brandVal: brandValidate,
      catOneVal: catOneFlag,
      catTwoVal: catTwoFlag,
      supplierVal: supplierValidate,
      productVal: productValidate,
    }));

    // Overall category validity still requires both when not allCategories
    const categoriesValid = allCategories || (catOneSelected && catTwoSelected);
    return brandValidate === true && categoriesValid === true && supplierValidate === true && productValidate === true;
  };

  const saveFunction = async () => {
    if (validationFun()) {
      if (!isEmpty(rightSidedata) && !isEmpty(leftSidedata)) {
        const mainData = {
          assignments: leftSidedata,
          rights: rightSidedata?.rights,
        };
        mainData.userId = paramId.userId;
        await dispatch(action.updateUserGroupsDetails(mainData, t));
        const data = paramId;
        await dispatch(action.getUserGroupsCardListbyid(data));
      }
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <div className="page-title-filter">
        <div className="title-container">
          <h4 className="title">{t('USER_MANAGEMENT.RIGHTS_MANAGEMENT')}</h4>
        </div>
        <div className="custom-File-Button d-flex ">
          <button className="filter-btn filter-button_selectall" aria-label="Save" type="button" onClick={saveFunction}>
            <span className="icon-Save mr-1" />
            {t('USER_MANAGEMENT.SAVE')}
          </button>
          <Link className="cursor-pointer text-dark text-decoration-none filter-btn filter-button_selectall" to="/settings/user-groups">
            <span className="mr-1" />
            {t('USER_MANAGEMENT.BACK')}
          </Link>
        </div>
      </div>
      <div className="groupsContainer">
        <div className="row m-0">
          <div className="left-section col-sm-4">
            <UserLeftSection data={groupsDetails} leftSectionData={leftSectionData} setParentDetails={setGroupsDetails} mainState={state} />
          </div>
          <div className="user-management-right-section col-sm-8 mt-2">
            <UserRightSection rightSectionData={rightSectionData} data={groupsDetailsRightSec} />
          </div>
        </div>
      </div>
    </>
  );
}

export default UserManagement;
